import { useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import type { FilterCategoryOption } from '@vtak/shared';
import { useTrackFilterStore } from '@/stores/track-filter-store';
import { cn } from '@/lib/utils';
import type { ActiveMapLayer } from './plugin-layer-manager';

/**
 * Per-layer track type-filter. Collapsed to a "Types · summary" row; expanded to
 * an "All" reset plus a checkbox per declared sub-type. Driven entirely by the
 * layer manifest's `filterCategories` — no domain-specific logic.
 */
export function LayerCategoryFilter({ layer }: { layer: ActiveMapLayer }): React.JSX.Element | null {
  const [open, setOpen] = useState(false);
  const options = layer.definition.filterCategories;
  if (!options || options.length === 0) return null;

  return (
    <div className="ml-7 mr-1 mt-0.5 mb-1">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-[10px] uppercase tracking-wider text-muted-foreground transition-colors hover:bg-echo-border/25"
      >
        <span className="font-chakra">Types</span>
        <FilterSummary layerId={layer.fullId} total={options.length} />
        <ChevronDown className={cn('h-3 w-3 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="mt-0.5 space-y-0.5">
          <AllRow layerId={layer.fullId} />
          {options.map((option) => (
            <CategoryCheckRow key={option.value} layerId={layer.fullId} option={option} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Pill summarising the active selection: "All" when unfiltered, else "N / total". */
function FilterSummary({ layerId, total }: { layerId: string; total: number }): React.JSX.Element {
  const size = useTrackFilterStore((s) => s.selectedFor(layerId)?.size ?? 0);
  const label = size === 0 ? 'All' : `${size}/${total}`;
  return (
    <span className="ml-auto rounded bg-echo-yellow/20 px-1 font-mono text-[9px] text-echo-yellow">
      {label}
    </span>
  );
}

/** "All" reset row — active when no specific sub-types are selected. */
function AllRow({ layerId }: { layerId: string }): React.JSX.Element {
  const active = useTrackFilterStore((s) => (s.selectedFor(layerId)?.size ?? 0) === 0);
  const clear = useTrackFilterStore((s) => s.clear);
  return (
    <CheckRow label="All" checked={active} onClick={() => clear(layerId)} />
  );
}

/** One sub-type checkbox row. */
function CategoryCheckRow({
  layerId,
  option,
}: {
  layerId: string;
  option: FilterCategoryOption;
}): React.JSX.Element {
  const checked = useTrackFilterStore((s) => s.isSelected(layerId, option.value));
  const toggle = useTrackFilterStore((s) => s.toggle);
  return (
    <CheckRow label={option.label} checked={checked} onClick={() => toggle(layerId, option.value)} />
  );
}

/** Shared checkbox row visual (echo-yellow box + label). */
function CheckRow({
  label,
  checked,
  onClick,
}: {
  label: string;
  checked: boolean;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-[11px] transition-colors hover:bg-echo-border/30"
    >
      <span
        className={cn(
          'flex h-3 w-3 shrink-0 items-center justify-center rounded-sm border transition-colors',
          checked ? 'border-echo-yellow bg-echo-yellow' : 'border-echo-border',
        )}
      >
        {checked && <Check className="h-2.5 w-2.5 text-black" strokeWidth={3} />}
      </span>
      <span className={cn('truncate', checked ? 'text-foreground' : 'text-muted-foreground')}>
        {label}
      </span>
    </button>
  );
}
